import { useQuery } from '@tanstack/react-query'
import { searchUsers } from '@transreport/pauk-rrms-api'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SearchableSelect, type SearchableSelectOption } from './SearchableSelect'

interface Props {
  id?: string
  /** Current display name shown in the input */
  value: string
  /** UUID of the currently selected user, or null if none */
  userId: string | null | undefined
  /** CRS station code — required by the API */
  stationCode: string
  /** Called when the user selects an option */
  onChange: (userId: string, displayName: string) => void
  /** Called when the user clears the selection */
  onClear: () => void
  placeholder?: string
  name?: string
  className?: string
  ariaLabel?: string
  ariaDescribedBy?: string
  ariaInvalid?: boolean
}

const FETCH_LIMIT = 20
const DEBOUNCE_MS = 300

export const StaffAutocomplete: React.FC<Props> = ({
  id,
  value,
  userId,
  stationCode,
  onChange,
  onClear,
  placeholder,
  name,
  className,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
}) => {
  const { t } = useTranslation()

  const [inputText, setInputText] = useState(value)
  // Debounced query string sent to the API
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Keep inputText in sync when the committed value changes externally (e.g. revert)
  useEffect(() => {
    setInputText(value)
  }, [value])

  // Debounce: update debouncedQuery after DEBOUNCE_MS of no typing
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedQuery(inputText), DEBOUNCE_MS)
    return () => clearTimeout(timeoutId)
  }, [inputText])

  // Focus-fetch: load all staff for the station (no q param, limit 20)
  const {
    data: focusData,
    isFetching: focusFetching,
    isError: focusError,
  } = useQuery({
    queryKey: ['searchUsers', stationCode, null] as const,
    queryFn: () => searchUsers({ station_code: stationCode, limit: FETCH_LIMIT }),
    enabled: !!stationCode,
    staleTime: 60_000,
  })

  // When the focus-fetch returns exactly 20, there may be more — require 2 chars minimum.
  // If fewer than 20 came back, we have the full list, so 1 char is fine.
  const minChars = focusData != null && focusData.length >= FETCH_LIMIT ? 2 : 1

  const queryEnabled =
    !!stationCode && debouncedQuery.length >= minChars && debouncedQuery !== value

  // Typed-query fetch
  const {
    data: searchData,
    isFetching: searchFetching,
    isError: searchError,
  } = useQuery({
    queryKey: ['searchUsers', stationCode, debouncedQuery] as const,
    queryFn: () =>
      searchUsers({
        station_code: stationCode,
        q: debouncedQuery,
        limit: FETCH_LIMIT,
      }),
    enabled: queryEnabled,
    staleTime: 30_000,
  })

  // Which results to show: typed search results if query meets threshold, else focus-fetch results
  const showingSearchResults = debouncedQuery.length >= minChars && debouncedQuery !== value
  const rawResults = (showingSearchResults ? searchData : focusData) ?? []
  const results = rawResults.filter((u) => u != null)
  const isFetching = showingSearchResults ? searchFetching : focusFetching
  const isError = showingSearchResults ? searchError : focusError

  const options = useMemo<SearchableSelectOption[]>(
    () =>
      results.map((user) => ({
        id: user.id,
        value: user.id,
        label: user.display_name,
      })),
    [results]
  )

  return (
    <SearchableSelect
      id={id}
      name={name}
      ariaLabel={ariaLabel}
      ariaDescribedBy={ariaDescribedBy}
      ariaInvalid={ariaInvalid}
      selectedValue={userId ?? null}
      selectedLabelFallback={value}
      options={options}
      onChange={(option) => {
        onChange(option.value, option.label)
      }}
      onClear={onClear}
      onInputValueChange={(nextValue) => {
        setInputText(nextValue)
      }}
      placeholder={placeholder}
      className={className}
      isLoading={isFetching}
      isError={isError}
      loadingText={t('resources.table.staffLoading')}
      errorText={t('resources.table.staffError')}
      emptyText={t('resources.table.staffNoResults')}
      filterOptions={false}
      clearButtonAriaLabel={t('resources.table.clearStaff', { name: inputText })}
    />
  )
}
